import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '@/shared/types/state';

import {
  AudienceStats,
  CampaignsState,
  DraftCampaign,
  DraftCampaignSubscriber,
  DraftCampaignSender,
  DraftVoiceSettings,
} from './actions';
import { ColumnMapping } from '../types/column-mapping';

/**
 * Get the campaigns state
 */
export const selectCampaignsState = (state: RootState): CampaignsState => state.campaigns;

/**
 * Get all campaigns
 */
export const selectCampaigns = (state: RootState) => selectCampaignsState(state).campaigns;

/**
 * Get the selected campaign
 */
export const selectSelectedCampaign = (state: RootState) =>
  selectCampaignsState(state).selectedCampaign;

/**
 * Get the draft campaign
 */
export const selectDraftCampaign = (state: RootState): Partial<DraftCampaign> | null =>
  selectCampaignsState(state).draftCampaign;

/**
 * Get draft campaign subscribers
 * Memoized to prevent unnecessary re-renders
 */
export const selectDraftSubscribers = createSelector(
  [(state: RootState) => state.campaigns.draftSubscribers],
  (draftSubscribers): DraftCampaignSubscriber[] => draftSubscribers || [],
  { devModeChecks: { identityFunctionCheck: 'never' } }
);

/**
 * Get draft campaign name
 */
export const selectDraftCampaignName = (state: RootState): string => {
  const draftCampaign = selectDraftCampaign(state);
  return draftCampaign?.name;
};

/**
 * Get draft campaign file name
 */
export const selectDraftCampaignFileName = (state: RootState): string | undefined => {
  return state.campaigns.subscribersFileName;
};

/**
 * Get draft campaign file size
 */
export const selectDraftCampaignFileSize = (state: RootState): number | undefined => {
  return state.campaigns.subscribersFileSize;
};

/**
 * Get whether there are draft subscribers
 */
export const selectHasDraftSubscribers = (state: RootState): boolean => {
  const subscribers = selectDraftSubscribers(state);
  return subscribers.length > 0;
};

/**
 * Quick-add never sets fileSize, CSV upload always does
 */
export const selectIsQuickAddSource = createSelector(
  [selectHasDraftSubscribers, selectDraftCampaignFileSize],
  (hasSubscribers, fileSize) => hasSubscribers && fileSize === undefined
);

/**
 * Get payment recovery draft sender (single sender, no primary/fallback like outreach)
 */
export const selectDraftPaymentRecoverySender = (state: RootState): DraftCampaignSender | null => {
  return state.campaigns.draftPaymentRecoverySender;
};

/**
 * Get multi-channel payment recovery draft senders
 */
export const selectDraftPaymentRecoverySenders = (
  state: RootState
): DraftCampaignSender[] | null => {
  return state.campaigns.draftPaymentRecoverySenders;
};

/**
 * Get draft campaign AI agent data (ID, version, and name)
 * Memoized to prevent unnecessary re-renders
 */
export const selectDraftAiAgentData = createSelector(
  [selectDraftCampaign],
  (
    draftCampaign
  ): {
    brain_parent_id: string | undefined;
    brain_version: number | undefined;
    brain_name: string | undefined;
  } => ({
    brain_parent_id: draftCampaign?.brain_parent_id,
    brain_version: draftCampaign?.brain_version,
    brain_name: draftCampaign?.brain_name,
  }),
  { devModeChecks: { identityFunctionCheck: 'never' } }
);

export const selectHasInitializedSubscribers = createSelector(
  [selectDraftCampaign],
  (draftCampaign): boolean => draftCampaign?.subscribers !== null
);

/**
 * Get draft campaign tags
 */
export const selectDraftCampaignTags = (state: RootState): { label: string; value: string }[] => {
  const draftCampaign = selectDraftCampaign(state);
  return draftCampaign?.tags || [];
};

/**
 * Get custom tags created during campaign setup
 * Memoized to prevent unnecessary re-renders
 */
export const selectCustomTags = createSelector(
  [(state: RootState) => state.campaigns.customTags],
  (customTags): { label: string; value: string }[] => customTags || [],
  { devModeChecks: { identityFunctionCheck: 'never' } }
);

/**
 * Get draft campaign compliance profile ID
 */
export const selectDraftComplianceProfileId = (state: RootState): string | undefined => {
  const draftCampaign = selectDraftCampaign(state);
  return draftCampaign?.compliance_profile_id;
};

/**
 * Get draft campaign type
 */
export const selectDraftCampaignType = (state: RootState): string | undefined => {
  const draftCampaign = selectDraftCampaign(state);
  return draftCampaign?.type;
};

/**
 * Get draft autonomous actions flag
 */
export const selectDraftAutonomousActions = (state: RootState): boolean => {
  const draftCampaign = selectDraftCampaign(state);
  return draftCampaign?.autonomous_actions ?? false;
};

/**
 * Check if the selected campaign has NBA config.
 * When true, messaging should show fallbacks with integration mappings (NBA flow).
 * When false, messaging should show standard fallback senders.
 */
export const selectHasNbaConfig = (state: RootState): boolean => {
  const campaign = selectSelectedCampaign(state);
  return campaign?.nba_config != null;
};

/**
 * Get draft selected channels for NBA messaging
 */
export const selectDraftSelectedChannels = (state: RootState): string[] | null => {
  return state.campaigns.draftSelectedChannels;
};

/**
 * Get draft goal for payment recovery campaign
 */
export const selectDraftPaymentRecoveryGoal = (state: RootState): string | null => {
  return state.campaigns.draftPaymentRecoveryGoal;
};

/**
 * Get draft guidelines for payment recovery campaign (markdown string)
 */
export const selectDraftPaymentRecoveryGuidelines = (state: RootState): string | null => {
  return state.campaigns.draftPaymentRecoveryGuidelines;
};

/**
 * Get the campaign-level voice opener settings shared across the message pages,
 * still tagged with the campaign they belong to
 */
export const selectDraftVoiceSettings = (state: RootState): DraftVoiceSettings | null => {
  return state.campaigns.draftVoiceSettings;
};

export const selectDraftOutreachGoal = (state: RootState): string | null => {
  return state.campaigns.draftOutreachGoal;
};

/**
 * Get draft audience stats
 */
export const selectDraftAudienceStats = (state: RootState): AudienceStats | null => {
  return state.campaigns.audienceStats;
};

/**
 * Get CSV headers for inline mapping
 */
export const selectCsvHeaders = (state: RootState): string[] | undefined => {
  return state.campaigns.csvHeaders;
};

/**
 * Get CSV mappings for inline mapping
 */
export const selectCsvMappings = (state: RootState): Record<string, ColumnMapping> | undefined => {
  return state.campaigns.csvMappings;
};

/**
 * Get CSV rows for re-transformation
 */
export const selectCsvRows = (state: RootState): string[][] | undefined => {
  return state.campaigns.csvRows;
};

/**
 * Get subscriber-context emit paths the user marked for mirroring under
 * `context.live_instructions.*` (MOV-15472).
 */
export const selectCsvLiveInstructionTargets = (state: RootState): string[] | undefined => {
  return state.campaigns.csvLiveInstructionTargets;
};
